import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Inbox,
  Plus,
  Search as SearchIcon,
} from 'lucide-react';
import {
  COMMAND_CENTER_FLAG_DEFAULTS,
  computeQueueCounts,
  formatDuration,
  formatRelativeDate,
  formatShortDate,
  matchesQueue,
  TASK_QUEUE_DESCRIPTORS,
  type Task,
  type TaskQueue,
} from '@oneup/shared';
import { tasksApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { TaskTagsControl } from '@/components/task-tags-control';
import { cn } from '@/lib/utils';
import { taskStatusVariant } from '@/lib/task-status';
import {
  CompactRecordList,
  CompactRecordListItem,
  WorkspaceSplitView,
} from '@/components/workspace';
import {
  ManagementBulkBar,
  ManagementCardGrid,
  ManagementEmptyState,
  ManagementErrorState,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementPriorityBadge,
  ManagementRecordCard,
  ManagementStatusBadge,
  ManagementTable,
  ManagementTableSurface,
} from '@/components/management';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { contextItem, pageContext, suggestion, useAlicePageContext } from '@/components/alice';
import { adminConfig } from '@/platform/config';
import { useUiStore } from '@/stores/ui';
import { TaskDetailWorkspace } from './TaskDrawer';

const VALID_QUEUES = new Set<TaskQueue>(TASK_QUEUE_DESCRIPTORS.map((q) => q.id));
const DEFAULT_QUEUE: TaskQueue = 'today';
const DEFAULT_SORT: TaskSort = 'last_modified';

type TaskSort = 'last_modified' | 'created' | 'status';

const TASK_SORT_OPTIONS: Array<{ value: TaskSort; label: string }> = [
  { value: 'last_modified', label: 'Last Modified' },
  { value: 'created', label: 'Created' },
  { value: 'status', label: 'Status' },
];

/**
 * Tasks view — Phase A (queue tabs, search, stat cards) + Phase B (drawer wiring).
 *
 * Queue tabs are interactive (URL-backed via `?queue=`). The full task list
 * is fetched once and filtered client-side via `matchesQueue()` — keeps queue
 * counts and the active queue in sync without re-fetching, and means the
 * drawer can navigate to a Closed task without changing the list query.
 *
 * Header search (`?search=`) filters the same list across name, notes, and
 * client. Both URL params compose: `/tasks?queue=overdue&search=acme`.
 *
 * Row click navigates to `/tasks/:id` (preserving query params); the
 * TaskDrawer renders when `taskId` is present in the URL.
 */
export function TasksView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { taskId } = useParams<{ taskId: string }>();
  const activeQueue = parseQueue(searchParams.get('queue'));
  const sort = parseTaskSort(searchParams.get('sort'));
  const search = (searchParams.get('search') ?? '').trim();
  const clientIdFilter = searchParams.get('clientId');
  const [viewMode, setViewMode] = usePersistentManagementView(
    'tasks',
    searchParams,
    setSearchParams,
  );
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 300 }),
  });

  const flagMutation = useMutation({
    mutationFn: ({ id, commandCenterFlags }: { id: string; commandCenterFlags: string[] }) =>
      tasksApi.patch(id, { commandCenterFlags }),
    onMutate: async ({ id, commandCenterFlags }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'all'] });
      const previous = queryClient.getQueryData<{ items: Task[] }>(['tasks', 'all']);
      if (previous) {
        queryClient.setQueryData(['tasks', 'all'], {
          ...previous,
          items: previous.items.map((task) =>
            task.id === id ? { ...task, commandCenterFlags } : task,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['tasks', 'all'], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['schema', 'field-options', 'ENT_Projects', 'Command Center Flags'] });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async ({ ids, patch }: { ids: string[]; patch: Partial<Task> }) => {
      await Promise.all(ids.map((id) => tasksApi.patch(id, patch)));
    },
    onMutate: async ({ ids, patch }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'all'] });
      const previous = queryClient.getQueryData<{ items: Task[] }>(['tasks', 'all']);
      if (previous) {
        const idSet = new Set(ids);
        queryClient.setQueryData(['tasks', 'all'], {
          ...previous,
          items: previous.items.map((task) =>
            idSet.has(task.id) ? { ...task, ...patch } : task,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['tasks', 'all'], context.previous);
    },
    onSuccess: () => setSelectedIds(new Set()),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['schema', 'field-options', 'ENT_Projects', 'Command Center Flags'] });
    },
  });

  const bulkFlagMutation = useMutation({
    mutationFn: async ({ tasks, flag }: { tasks: Task[]; flag: string }) => {
      await Promise.all(
        tasks.map((task) => {
          const commandCenterFlags = task.commandCenterFlags.includes(flag)
            ? task.commandCenterFlags
            : [...task.commandCenterFlags, flag];
          return tasksApi.patch(task.id, { commandCenterFlags });
        }),
      );
    },
    onMutate: async ({ tasks, flag }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'all'] });
      const previous = queryClient.getQueryData<{ items: Task[] }>(['tasks', 'all']);
      if (previous) {
        const ids = new Set(tasks.map((task) => task.id));
        queryClient.setQueryData(['tasks', 'all'], {
          ...previous,
          items: previous.items.map((task) =>
            ids.has(task.id) && !task.commandCenterFlags.includes(flag)
              ? { ...task, commandCenterFlags: [...task.commandCenterFlags, flag] }
              : task,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['tasks', 'all'], context.previous);
    },
    onSuccess: () => setSelectedIds(new Set()),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['schema', 'field-options', 'ENT_Projects', 'Command Center Flags'] });
    },
  });

  const openTask = useCallback(
    (id: string) => {
      const qs = searchParams.toString();
      navigate(qs ? `/tasks/${id}?${qs}` : `/tasks/${id}`);
    },
    [navigate, searchParams],
  );
  const closeTask = useCallback(() => {
    const qs = searchParams.toString();
    navigate(qs ? `/tasks?${qs}` : '/tasks');
  }, [navigate, searchParams]);

  const tasks = data?.items ?? [];
  const selectedTask = taskId ? tasks.find((task) => task.id === taskId) : undefined;
  const leafTasks = useMemo(() => tasks.filter(isLeafTask), [tasks]);
  const leafOpenTasks = useMemo(() => leafTasks.filter(isOpenTask), [leafTasks]);
  const counts = useMemo(() => {
    const openCounts = computeQueueCounts(leafOpenTasks);
    return {
      ...openCounts,
      done_this_week: leafTasks.filter((task) => matchesQueue('done_this_week', task)).length,
    };
  }, [leafOpenTasks, leafTasks]);

  const filteredTasks = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    const sourceTasks = activeQueue === 'done_this_week' ? leafTasks : leafOpenTasks;
    const filtered = sourceTasks.filter((task) => {
      if (!matchesQueue(activeQueue, task)) return false;
      if (clientIdFilter && task.client?.id !== clientIdFilter) return false;
      if (!lowerSearch) return true;
      return matchesSearch(task, lowerSearch);
    });

    return sortTasks(filtered, sort);
  }, [activeQueue, clientIdFilter, leafOpenTasks, leafTasks, search, sort]);
  const selectedIndex = taskId ? filteredTasks.findIndex((task) => task.id === taskId) : -1;
  const goToTaskAt = useCallback(
    (index: number) => {
      const task = filteredTasks[index];
      if (task) openTask(task.id);
    },
    [filteredTasks, openTask],
  );

  const footerBaseCount = activeQueue === 'done_this_week' ? leafTasks.length : leafOpenTasks.length;
  const footerBaseLabel = activeQueue === 'done_this_week' ? 'leaf tasks' : 'open leaf tasks';

  const selectedTasks = useMemo(
    () => filteredTasks.filter((task) => selectedIds.has(task.id)),
    [filteredTasks, selectedIds],
  );
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: 'tasks',
        pageTitle: 'Tasks',
        recordType: taskId ? 'task' : undefined,
        recordId: taskId,
        recordLabel: selectedTask?.name ?? taskId,
        organizationId: selectedTask?.client?.id,
        organizationLabel: selectedTask?.client?.name,
        contextItems: [
          contextItem(
            'tasks-page',
            'page',
            'Tasks',
            'Daily operating queue with queue, search, bulk action, and task detail context.',
          ),
          ...(taskId
            ? [
                contextItem(
                  taskId,
                  'task',
                  selectedTask?.name ?? taskId,
                  selectedTask?.notes ?? selectedTask?.status,
                ),
              ]
            : []),
        ],
        suggestedActions: taskId
          ? [
              suggestion('task-draft-client-update', 'Draft client update', 'Draft a client-safe update from this task.', 'composer'),
              suggestion('task-summarize-notes', 'Summarize latest notes', 'Summarize visible task notes and context.'),
              suggestion('task-process-notes', 'Process notes', 'Extract blockers, decisions, and next actions.'),
            ]
          : [],
      }),
    [selectedTask, taskId],
  );
  useAlicePageContext(aliceContext);
  const selectedVisibleIds = selectedTasks.map((task) => task.id);
  const allVisibleSelected =
    filteredTasks.length > 0 && filteredTasks.every((task) => selectedIds.has(task.id));

  const toggleSelected = useCallback((id: string, selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(
    (selected: boolean) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        for (const task of filteredTasks) {
          if (selected) next.add(task.id);
          else next.delete(task.id);
        }
        return next;
      });
    },
    [filteredTasks],
  );

  const applyBulkFlag = useCallback(
    (flag: string) => {
      if (selectedTasks.length === 0) return;
      bulkFlagMutation.mutate({ tasks: selectedTasks, flag });
    },
    [bulkFlagMutation, selectedTasks],
  );

  const setQueue = useCallback(
    (queueId: TaskQueue) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (queueId === DEFAULT_QUEUE) params.delete('queue');
          else params.set('queue', queueId);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const clearSearch = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('search');
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSort = useCallback(
    (sortId: TaskSort) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (sortId === DEFAULT_SORT) params.delete('sort');
          else params.set('sort', sortId);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  if (isLoading) {
    return <ManagementLoadingState metricCount={3} />;
  }

  if (error) {
    return (
      <ManagementPage>
        <ManagementErrorState title="Couldn't load tasks" error={error} />
        <p className="rounded-lg border border-border/70 bg-card p-3 text-xs text-muted-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]">
          Tip: in dev, make sure the Worker is running (<code>npm run dev:worker</code>) and your{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">.dev.vars</code>{' '}
          file has a valid <code>AIRTABLE_API_KEY</code>.
        </p>
      </ManagementPage>
    );
  }

  const activeDescriptor = TASK_QUEUE_DESCRIPTORS.find((q) => q.id === activeQueue);
  const page = MANAGEMENT_PAGES.tasks;

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={
          <span className="flex flex-wrap gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">Daily operating queue.</span>
            <span>Open and In Progress tasks, most recently modified first.</span>
            {activeDescriptor && activeQueue !== DEFAULT_QUEUE && (
              <span className="text-muted-foreground/80">{activeDescriptor.description}</span>
            )}
          </span>
        }
        source={page.source}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search task, notes, or client',
        }}
        filters={[
          {
            id: 'queue',
            label: 'Queue',
            value: activeQueue,
            onChange: (value) => setQueue(value as TaskQueue),
            options: TASK_QUEUE_DESCRIPTORS.filter((q) => q.defaultVisible).map((q) => ({
              value: q.id,
              label: q.label,
              count: counts[q.id],
              title: q.description,
            })),
            widthClassName: 'w-full sm:w-[240px]',
          },
          {
            id: 'sort',
            label: 'Sort by',
            value: sort,
            onChange: (value) => setSort(value as TaskSort),
            options: TASK_SORT_OPTIONS,
            widthClassName: 'w-full sm:w-[190px]',
          },
        ]}
        primaryAction={{
          label: 'Create Task',
          icon: Plus,
          onClick: () => openCreateDrawer({ type: 'task' }),
        }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <ManagementBulkBar
        selectedCount={selectedVisibleIds.length}
        onClear={() => setSelectedIds(new Set())}
      >
        {COMMAND_CENTER_FLAG_DEFAULTS.map((flag) => (
          <Button
            key={flag}
            type="button"
            size="sm"
            variant="outline"
            disabled={bulkMutation.isPending || bulkFlagMutation.isPending}
            onClick={() => applyBulkFlag(flag)}
            className="h-8"
          >
            {flag}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={bulkMutation.isPending || bulkFlagMutation.isPending}
          onClick={() =>
            bulkMutation.mutate({ ids: selectedVisibleIds, patch: { status: 'Closed' } })
          }
          className="h-8"
        >
          Mark Closed
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={bulkMutation.isPending || bulkFlagMutation.isPending}
          onClick={() =>
            bulkMutation.mutate({ ids: selectedVisibleIds, patch: { status: 'Archived' } })
          }
          className="h-8"
        >
          Archive
        </Button>
      </ManagementBulkBar>
      <WorkspaceSplitView
        footer={
          <ManagementFooterSummary>
            Showing <span className="tabular-nums text-foreground">{filteredTasks.length}</span>
            {footerBaseCount !== filteredTasks.length && (
              <>
                {' '}of <span className="tabular-nums">{footerBaseCount}</span> {footerBaseLabel}
              </>
            )}
          </ManagementFooterSummary>
        }
        detail={
          taskId && (
            <TaskDetailWorkspace
              taskId={taskId}
              onClose={closeTask}
              listTitle="Tasks"
              selectedIndex={selectedIndex}
              totalRecords={filteredTasks.length}
              onPrevious={() => goToTaskAt(selectedIndex - 1)}
              onNext={() => goToTaskAt(selectedIndex + 1)}
            />
          )
        }
      >
      {taskId ? (
        <TaskCompactList
          tasks={filteredTasks}
          activeTaskId={taskId}
          onOpen={openTask}
        />
      ) : viewMode === 'table' ? (
        <ManagementTable>
          <table className="w-full min-w-[1080px] text-left text-[13px]">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(event) => toggleAllVisible(event.currentTarget.checked)}
                    aria-label="Select all visible tasks"
                    className="accent-primary"
                  />
                </th>
                <th>Task</th>
                <th>Client</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Estimate</th>
                <th>Tracked</th>
                <th>Due</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-12">
                    <EmptyState
                      queue={activeQueue}
                      hasSearch={Boolean(search)}
                      onClearSearch={clearSearch}
                      onSwitchQueue={setQueue}
                    />
                  </td>
                </tr>
              )}
              {filteredTasks.map((task) => (
                <tr
                  key={task.id}
                  onClick={() => openTask(task.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openTask(task.id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${task.name}`}
                  className={cn(
                    'cursor-pointer outline-none transition-colors',
                    'focus-visible:bg-muted/35',
                    selectedIds.has(task.id) && 'bg-primary/[0.025]',
                    taskId === task.id && 'bg-primary/[0.045] shadow-[inset_2px_0_0_hsl(var(--primary))]',
                  )}
                >
                  <td onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(task.id)}
                      onChange={(event) => toggleSelected(task.id, event.currentTarget.checked)}
                      aria-label={`Select ${task.name}`}
                      className="accent-primary"
                    />
                  </td>
                  <td>
                    <div className="flex items-start gap-1.5 font-semibold text-foreground">
                      <span className="min-w-0 leading-snug">{task.name}</span>
                    </div>
                    {task.statusLastModified && (
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        Updated {formatRelativeDate(task.statusLastModified)}
                      </div>
                    )}
                  </td>
                  <td>
                    {task.client?.name ? (
                      <div className="flex items-center gap-2">
                        {task.client.logoUrl ? (
                          <img
                            src={task.client.logoUrl}
                            alt=""
                            className="h-7 w-7 rounded-md border border-border/60 object-cover"
                          />
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-[10px] font-semibold text-primary">
                            {task.client.name.slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <span className="truncate text-muted-foreground">{task.client.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td>
                    {task.status ? (
                      <ManagementStatusBadge variant={taskStatusVariant(task.semanticStatus)}>
                        {task.status}
                      </ManagementStatusBadge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td>
                    {task.priority ? (
                      <ManagementPriorityBadge variant={priorityToVariant(task.semanticPriority)}>
                        {task.priority}
                      </ManagementPriorityBadge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="text-muted-foreground">
                    {formatDuration(task.estimateMinutes)}
                  </td>
                  <td className="text-muted-foreground">
                    {formatDuration(task.trackedMinutes)}
                  </td>
                  <td className="text-muted-foreground">
                    {task.targetDate ? formatShortDate(task.targetDate) : '-'}
                  </td>
                  <td>
                    <TaskTagsControl
                      value={task.commandCenterFlags}
                      compact
                      disabled={flagMutation.isPending}
                      onChange={(commandCenterFlags) =>
                        flagMutation.mutate({ id: task.id, commandCenterFlags })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ManagementTable>
      ) : (
        <TaskCardView
          tasks={filteredTasks}
          selectedIds={selectedIds}
          activeTaskId={taskId}
          flagPending={flagMutation.isPending}
          queue={activeQueue}
          search={search}
          onOpen={openTask}
          onSelect={toggleSelected}
          onFlagChange={(id, commandCenterFlags) =>
            flagMutation.mutate({ id, commandCenterFlags })
          }
          onClearSearch={clearSearch}
          onSwitchQueue={setQueue}
        />
      )}

      </WorkspaceSplitView>
    </ManagementPage>
  );
}

function TaskCompactList({
  tasks,
  activeTaskId,
  onOpen,
}: {
  tasks: Task[];
  activeTaskId: string;
  onOpen: (id: string) => void;
}) {
  if (tasks.length === 0) {
    return (
      <ManagementTableSurface>
        <ManagementEmptyState
          icon={Inbox}
          title="No tasks match this view."
          body="Close the detail pane or clear filters to pick from another task set."
        />
      </ManagementTableSurface>
    );
  }

  return (
    <CompactRecordList title="Visible Tasks">
      {tasks.map((task) => (
        <CompactRecordListItem
          key={task.id}
          selected={activeTaskId === task.id}
          onSelect={() => onOpen(task.id)}
          title={task.name}
          titleLines={2}
          badges={
            <>
              {task.status && (
                <ManagementStatusBadge variant={taskStatusVariant(task.semanticStatus)}>
                  {task.status}
                </ManagementStatusBadge>
              )}
              {task.priority && (
                <ManagementPriorityBadge variant={priorityToVariant(task.semanticPriority)}>
                  {task.priority}
                </ManagementPriorityBadge>
              )}
            </>
          }
          meta={
            <>
              <span className="max-w-full truncate">{task.client?.name ?? 'No client'}</span>
              <span>Tracked: {formatDuration(task.trackedMinutes)}</span>
              {task.targetDate && <span>Due: {formatShortDate(task.targetDate)}</span>}
            </>
          }
        />
      ))}
    </CompactRecordList>
  );
}

function TaskCardView({
  tasks,
  selectedIds,
  activeTaskId,
  flagPending,
  queue,
  search,
  onOpen,
  onSelect,
  onFlagChange,
  onClearSearch,
  onSwitchQueue,
}: {
  tasks: Task[];
  selectedIds: Set<string>;
  activeTaskId: string | undefined;
  flagPending: boolean;
  queue: TaskQueue;
  search: string;
  onOpen: (id: string) => void;
  onSelect: (id: string, selected: boolean) => void;
  onFlagChange: (id: string, commandCenterFlags: string[]) => void;
  onClearSearch: () => void;
  onSwitchQueue: (queue: TaskQueue) => void;
}) {
  if (tasks.length === 0) {
    return (
      <ManagementTableSurface>
        <ManagementEmptyState
          icon={search ? SearchIcon : Inbox}
          title={search ? 'Nothing matches your search.' : QUEUE_EMPTY_STATES[queue].title}
          body={
            search
              ? `Tasks are filtered by the ${queueLabel(queue)} queue plus your search.`
              : QUEUE_EMPTY_STATES[queue].body
          }
          action={
            search ? (
              <div className="mt-1 flex gap-2">
                <EmptyAction onClick={onClearSearch} label="Clear search" />
                {queue !== 'all' && (
                  <EmptyAction onClick={() => onSwitchQueue('all')} label="Search all tasks" />
                )}
              </div>
            ) : QUEUE_EMPTY_STATES[queue].cta ? (
              <EmptyAction
                onClick={() => onSwitchQueue(QUEUE_EMPTY_STATES[queue].cta!.queue)}
                label={QUEUE_EMPTY_STATES[queue].cta!.label}
              />
            ) : undefined
          }
        />
      </ManagementTableSurface>
    );
  }

  return (
    <ManagementCardGrid>
      {tasks.map((task) => (
        <ManagementRecordCard
          key={task.id}
          role="button"
          tabIndex={0}
          selected={selectedIds.has(task.id)}
          active={activeTaskId === task.id}
          className={cn(
            'group cursor-pointer overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          onClick={() => onOpen(task.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onOpen(task.id);
            }
          }}
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={selectedIds.has(task.id)}
              aria-label={`Select ${task.name}`}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => onSelect(task.id, event.currentTarget.checked)}
              className="mt-1 accent-primary"
            />
            <div className="min-w-0 flex-1 space-y-2">
              <div>
                <div className="line-clamp-2 text-sm font-semibold text-foreground">
                  {task.name}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {task.status && (
                    <ManagementStatusBadge variant={taskStatusVariant(task.semanticStatus)}>
                      {task.status}
                    </ManagementStatusBadge>
                  )}
                  {task.priority && (
                    <ManagementPriorityBadge variant={priorityToVariant(task.semanticPriority)}>
                      {task.priority}
                    </ManagementPriorityBadge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <TaskCardFact label="Estimate" value={formatDuration(task.estimateMinutes)} />
                <TaskCardFact label="Tracked" value={formatDuration(task.trackedMinutes)} />
                <TaskCardFact
                  label="Due"
                  value={task.targetDate ? formatShortDate(task.targetDate) : '-'}
                />
              </div>

              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0 truncate text-[12px] text-muted-foreground">
                  {task.client?.name ?? 'No client'}
                </div>
                {task.statusLastModified && (
                  <div className="whitespace-nowrap text-[11px] text-muted-foreground">
                    {formatRelativeDate(task.statusLastModified)}
                  </div>
                )}
              </div>

              <div onClick={(event) => event.stopPropagation()}>
                <TaskTagsControl
                  value={task.commandCenterFlags}
                  compact
                  disabled={flagPending}
                  onChange={(commandCenterFlags) => onFlagChange(task.id, commandCenterFlags)}
                />
              </div>
            </div>
          </div>
        </ManagementRecordCard>
      ))}
    </ManagementCardGrid>
  );
}

function TaskCardFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/35 px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-[12px] font-medium text-foreground">{value}</div>
    </div>
  );
}

function parseQueue(value: string | null): TaskQueue {
  if (!value) return DEFAULT_QUEUE;
  if (VALID_QUEUES.has(value as TaskQueue)) return value as TaskQueue;
  return DEFAULT_QUEUE;
}

function parseTaskSort(value: string | null): TaskSort {
  if (value === 'created' || value === 'status') return value;
  return DEFAULT_SORT;
}

function matchesSearch(task: Task, lowerSearch: string): boolean {
  if (task.name?.toLowerCase().includes(lowerSearch)) return true;
  if (task.notes?.toLowerCase().includes(lowerSearch)) return true;
  if (task.client?.name?.toLowerCase().includes(lowerSearch)) return true;
  return false;
}

function isLeafTask(task: Task): boolean {
  const hasChildren = (task.childTaskIds?.length ?? 0) > 0;
  return !hasChildren;
}

function isOpenTask(task: Task): boolean {
  const status = task.status.trim().toLowerCase();
  return task.semanticStatus !== 'done' && status !== 'closed' && status !== 'archived' && status !== 'done';
}

function sortTasks(tasks: Task[], sort: TaskSort): Task[] {
  const sorted = [...tasks];
  if (sort === 'created') {
    return sorted.sort((a, b) =>
      compareDesc(a.createdAt ?? a.statusLastModified, b.createdAt ?? b.statusLastModified),
    );
  }
  if (sort === 'status') {
    return sorted.sort((a, b) => {
      const statusCompare = (a.status || a.semanticStatus).localeCompare(
        b.status || b.semanticStatus,
        undefined,
        { sensitivity: 'base' },
      );
      if (statusCompare !== 0) return statusCompare;
      return compareDesc(a.statusLastModified, b.statusLastModified);
    });
  }
  return sorted.sort((a, b) => compareDesc(a.statusLastModified, b.statusLastModified));
}

/** Sort comparators that tolerate undefined values. */
function compareDesc(a: string | undefined, b: string | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return b.localeCompare(a);
}

interface EmptyStateProps {
  queue: TaskQueue;
  hasSearch: boolean;
  onClearSearch: () => void;
  onSwitchQueue: (q: TaskQueue) => void;
}

function EmptyState({ queue, hasSearch, onClearSearch, onSwitchQueue }: EmptyStateProps) {
  if (hasSearch) {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <SearchIcon className="h-6 w-6 text-muted-foreground/50" aria-hidden />
        <p className="text-sm font-medium text-foreground">Nothing matches your search.</p>
        <p className="text-[12px] text-muted-foreground">
          Tasks are filtered by the <strong>{queueLabel(queue)}</strong> queue plus your search.
        </p>
        <div className="mt-1 flex gap-2">
          <EmptyAction onClick={onClearSearch} label="Clear search" />
          {queue !== 'all' && (
            <EmptyAction onClick={() => onSwitchQueue('all')} label="Search all tasks" />
          )}
        </div>
      </div>
    );
  }

  const empty = QUEUE_EMPTY_STATES[queue];
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <Inbox className="h-6 w-6 text-muted-foreground/50" aria-hidden />
      <p className="text-sm font-medium text-foreground">{empty.title}</p>
      {empty.body && <p className="max-w-md text-[12px] text-muted-foreground">{empty.body}</p>}
      {empty.cta && (
        <div className="mt-1">
          <EmptyAction onClick={() => onSwitchQueue(empty.cta!.queue)} label={empty.cta.label} />
        </div>
      )}
    </div>
  );
}

function EmptyAction({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
    >
      {label}
      <ArrowRight className="h-3 w-3" aria-hidden />
    </button>
  );
}

const QUEUE_EMPTY_STATES: Record<
  TaskQueue,
  { title: string; body?: string; cta?: { queue: TaskQueue; label: string } }
> = {
  today: {
    title: 'No open or in-progress tasks.',
    body: 'Nothing is currently Open or In Progress. Pick something from Triage to start, or clear inbox in Done This Week.',
    cta: { queue: 'needs_triage', label: 'Open Triage' },
  },
  overdue: {
    title: 'No overdue tasks.',
    body: 'All target dates are honored. Try the Today queue to see what is current.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  needs_triage: {
    title: 'Inbox zero on triage.',
    body: 'Nothing new and unsorted. Anything that lands here will need a status, a client, or a next action.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  in_progress: {
    title: 'Nothing in motion right now.',
    body: 'No tasks are marked In Progress. Pick something from Today and start it.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  waiting_on_client: {
    title: 'No client blockers.',
    body: 'Everything is on us right now. Good place to be.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  review: {
    title: 'No review queue.',
    body: 'Nothing is awaiting review or sign-off.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  needs_client_update: {
    title: 'All clients are current.',
    body: 'No active tasks have gone dark on a client.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  ready_to_bill: {
    title: 'No unbilled tracked time.',
    body: 'Either everything tracked is already invoiced or marked not-billable.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  internal: {
    title: 'No internal tasks.',
    body: 'Nothing flagged as One Up internal or non-billable.',
    cta: { queue: 'today', label: 'Go to Today' },
  },
  done_this_week: {
    title: 'Nothing closed this week yet.',
    body: 'Closed tasks from the last 7 days will show here.',
    cta: { queue: 'today', label: 'Back to Today' },
  },
  all: {
    title: 'No tasks.',
    body: 'Quick Capture (top right) will land your first one here.',
  },
};

function queueLabel(q: TaskQueue): string {
  return TASK_QUEUE_DESCRIPTORS.find((d) => d.id === q)?.label ?? q;
}

function priorityToVariant(p: string): 'destructive' | 'warning' | 'muted' {
  if (p === 'urgent') return 'destructive';
  if (p === 'high') return 'warning';
  return 'muted';
}
